import type {
  CommercialAcceptRequest,
  CommercialCommitmentOwnerPage,
  CommercialDocumentOwnerView,
  CommercialOfferOwnerView,
  CommercialWorkspaceReadiness,
} from "@bw/shared/types/generated";
import { useEffect, useState } from "react";
import { isModuleOfferAcceptanceRequest, sameModuleOfferLocator } from "@bw/shared/api";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ModuleOfferSummary } from "./ModuleOfferSummary";
import {
  ModuleOfferEconomics,
  moduleOfferGateLabels,
} from "@bw/shared/components";

export interface ModuleOfferTermsProps {
  offer: CommercialOfferOwnerView;
}
export function ModuleOfferTerms({ offer }: ModuleOfferTermsProps) {
  return (
    <Stack spacing={1.5}>
      {offer.terms_format === "software_terms_v2"
        ? <ModuleOfferEconomics termsFormat={offer.terms_format} terms={offer.economics} />
        : <ModuleOfferEconomics termsFormat={offer.terms_format} terms={offer.economics} />}
      <Typography variant="body2" color="text.secondary">
        Effective {new Date(offer.effective_from).toLocaleString()}
        {offer.effective_until
          ? ` until ${new Date(offer.effective_until).toLocaleString()}`
          : ""}
        .
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Accepting this offer does not activate a licence, collect money or grant
        access to tools.
      </Typography>
    </Stack>
  );
}

/** Exact source bytes/hash remain independent of inert text presentation. */
export interface ModuleOfferDocumentProps {
  document: CommercialDocumentOwnerView;
  headingLevel?: "h2" | "h3";
}
export function ModuleOfferDocument({ document, headingLevel = "h3" }: ModuleOfferDocumentProps) {
  const longDocument = document.content.length > 600 || document.content.split("\n").length > 12;
  return <Stack spacing={1.5} aria-label="Exact immutable document">
    <Box>
      <Typography component={headingLevel} variant="h6" sx={{ fontWeight: 700 }}>Agreement content</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        The exact text you are reviewing and accepting.
      </Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">
      {longDocument ? "Full agreement text · Scroll within this region to continue reading." : "Full agreement text"}
    </Typography>
    <Box role="region" aria-label="Full agreement text" tabIndex={0}
      sx={{ maxHeight: longDocument ? 360 : "none", overflow: longDocument ? "auto" : "visible", bgcolor: "action.hover", borderRadius: 1.5, p: 2,
        outline: "none", "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}` } }}>
      <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontFamily: "inherit", lineHeight: 1.65 }}>
        {document.content}
      </Typography>
    </Box>
    <Accordion disableGutters elevation={0} sx={{ bgcolor: "transparent", "&::before": { display: "none" }, borderTop: 1, borderColor: "divider" }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Technical document details</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Stack spacing={0.25}>
          <Typography variant="caption" color="text.secondary">{document.media_type} · {document.language} · SHA-256</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere", fontFamily: "monospace" }}>
            {document.content_sha256}
          </Typography>
        </Stack>
      </AccordionDetails>
    </Accordion>
  </Stack>;
}

export interface ModuleOfferAcceptanceProps {
  offer: CommercialOfferOwnerView;
  attempt: CommercialAcceptRequest | null;
  isPending: boolean;
  error: string | null;
  onConfirm: (request: CommercialAcceptRequest) => void;
}
export function ModuleOfferAcceptance({
  offer,
  attempt,
  isPending,
  error,
  onConfirm,
}: ModuleOfferAcceptanceProps) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CommercialAcceptRequest | null>(
    attempt,
  );
  const [now, setNow] = useState(Date.now());
  const endsAt = offer.effective_until
    ? Date.parse(offer.effective_until)
    : null;
  useEffect(() => {
    if (endsAt === null || !Number.isFinite(endsAt) || endsAt <= Date.now())
      return;
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.max(0, Math.min(endsAt - Date.now(), 2_147_483_647)),
    );
    return () => clearTimeout(timer);
  }, [endsAt, now]);
  const policy = offer.terms_format === "software_terms_v2" && offer.publication_format === "module_policy_v1"
    ? offer.lifecycle_terms : null;
  const matchesOffer = (value: unknown): value is CommercialAcceptRequest => isModuleOfferAcceptanceRequest(value)
    && policy !== null && sameModuleOfferLocator(value.offer_version_id, offer.offer_version_id)
    && value.expected_document_sha256 === offer.document.content_sha256
    && value.expected_owner_terms_fingerprint === policy.owner_terms_fingerprint;
  const exactAttempt = matchesOffer(attempt);
  const freshAvailable =
    policy !== null &&
    offer.availability === "available" &&
    (endsAt === null || (Number.isFinite(endsAt) && now < endsAt));
  const canSubmit = !isPending && policy !== null && (attempt ? exactAttempt : freshAvailable);
  const supplied = attempt ?? request;
  return (
    <Stack spacing={1}>
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {!freshAvailable && !exactAttempt ? (
        <Typography variant="body2">
          {offer.terms_format === "legacy_fixed_v1" || policy === null
            ? "Historical terms remain readable. A new approved policy-bound edition is required for a new acceptance and purchase."
            : offer.availability === "upcoming"
            ? "This edition is upcoming and cannot be accepted yet."
            : "This edition is expired or not available for new acceptance."}
        </Typography>
      ) : null}
      <Button
        size="small"
        variant="contained"
        disabled={!canSubmit}
        onClick={() => {
          if (!canSubmit || policy === null) return;
          setRequest(
            attempt ?? Object.freeze({
              offer_version_id: offer.offer_version_id,
              expected_document_sha256: offer.document.content_sha256,
              expected_owner_terms_fingerprint: policy.owner_terms_fingerprint,
              idempotency_key: crypto.randomUUID(),
            }),
          );
          setOpen(true);
        }}
      >
        {attempt ? "Retry original acceptance" : "Accept module offer"}
      </Button>
      <Dialog
        open={open}
        transitionDuration={{ exit: 0 }}
        onClose={() => !isPending && setOpen(false)}
        slotProps={{
          paper: {
            role: "alertdialog",
            "aria-labelledby": "module-accept-title",
          },
        }}
      >
        <DialogTitle id="module-accept-title">
          Accept these exact offer terms?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {offer.title}. Acceptance records the reviewed document and economic
            terms and original lifecycle policies, without payment or licence activation.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            disabled={isPending}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!canSubmit || !matchesOffer(supplied)}
            onClick={() => {
              if (!canSubmit || !matchesOffer(supplied)) return;
              setOpen(false);
              onConfirm(supplied);
            }}
          >
            Confirm acceptance
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export interface ModuleCommitmentHistoryProps {
  page: CommercialCommitmentOwnerPage;
  onOpen: (acceptanceId: string) => void;
  onBrowseOffers: () => void;
}
export function ModuleCommitmentHistory({
  page,
  onOpen,
  onBrowseOffers,
}: ModuleCommitmentHistoryProps) {
  return (
    <Stack spacing={2.5}>
      {!page.items.length ? (
        page.total === 0 && page.offset === 0 ? <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
          <Typography>No accepted module offers yet.</Typography>
          <Button size="small" variant="contained" onClick={() => onBrowseOffers()}>Browse module offers</Button>
        </Stack> : <Typography>No accepted module offers on this page.</Typography>
      ) : (
        page.items.map((item) => (
          <ModuleOfferSummary key={item.acceptance_id} offer={item.offer} accepted={{
            accepted_at: item.accepted_at,
            accepted_by_display_name: item.accepted_by_display_name,
            onOpen: () => onOpen(item.acceptance_id),
          }} />
        ))
      )}
    </Stack>
  );
}

export interface CommercialReadinessFactsProps {
  readiness: CommercialWorkspaceReadiness;
  onRetry: () => void;
}
export function CommercialReadinessFacts({
  readiness,
  onRetry,
}: CommercialReadinessFactsProps) {
  const reasons: Record<string, string> = {
    baseline_allocation_unresolved:
      "Baseline allocation needs platform review.",
    module_authoring_unresolved:
      "Module authoring allocation remains unresolved.",
    paid_activation_disabled:
      "Software purchase and activation are not enabled.",
    commercial_currency_unresolved: "Organization currency remains unresolved.",
    stripe_onboarding_incomplete: "Player payment setup is incomplete.",
    operational_setup_unknown: "Operational setup evidence is unknown.",
    facility_readiness_unavailable:
      "Facility readiness evidence is unavailable.",
  };
  return (
    <Stack spacing={1.5}>
      <Typography>Workspace: {readiness.baseline_workspace}</Typography>
      <Typography>Public listing: {readiness.public_listing}</Typography>
      <Typography>Module authoring: {readiness.module_authoring}</Typography>
      <Typography>
        Player money collection: {readiness.player_money_collection}
      </Typography>
      {readiness.blockers.map((code, index) => (
        <Typography key={index} variant="body2" color="text.secondary">
          {reasons[code] ??
            "Required source evidence remains unresolved. Ask the platform for review."}
        </Typography>
      ))}
      {readiness.unresolved_gates.map((gate) => (
        <Typography key={gate} variant="body2">
          Unresolved: {moduleOfferGateLabels[gate] ?? "Source policy"}
        </Typography>
      ))}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button size="small" variant="outlined" href="/settings/org">
          Review organization settings
        </Button>
        <Button size="small" variant="outlined" href="/settings/facility">
          Review facility settings
        </Button>
        {readiness.blockers.includes("stripe_onboarding_incomplete") ? (
          <Button size="small" variant="outlined" href="/setup/stripe">
            Review payment setup
          </Button>
        ) : null}
        <Button size="small" variant="outlined" onClick={onRetry}>
          Refresh readiness
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        These separate source facts do not enable software purchase or
        activation.
      </Typography>
    </Stack>
  );
}
